import { MATCHMAKING_ENABLED } from 'src/pages/dashboard/playExperiences'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import ThumbsUpDownRoundedIcon from '@mui/icons-material/ThumbsUpDownRounded'
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded'
import { TransitionProps } from '@mui/material/transitions'
import { GlassButton, GlassPanel } from '@components/common/Glass'
import {
  altColorDarkText,
  altColorLighter,
  borderColor,
  canvasColor,
  gold,
  headingFont,
  helperTextColor,
  mainColorDarker,
  mainColorLighter,
  navy,
} from 'src/ui/theme/tokens'
import { GameSounds } from 'src/ui/sound/types'

type HowToPlayDialogProps = {
  open: boolean
  fullScreen?: boolean
  sounds?: Pick<GameSounds, 'click' | 'swipe'>
  onClose: () => void
}

const steps = [
  {
    title: 'Pick your mode',
    description: 'Play solo, challenge friends, or host the room.',
  },
  {
    title: 'Find your list',
    description: 'Browse community trivia and polls, or create your own.',
  },
  {
    title: 'Make your move',
    description: 'Sort the full list or hunt for the next top-ranked item.',
  },
]
const modes = [
  {
    title: 'Solo Play',
    eyebrow: 'YOUR BRAIN. YOUR PACE.',
    icon: PersonRoundedIcon,
    background: mainColorLighter,
    color: mainColorDarker,
    description:
      'Drag items into rank order and submit. Correct items lock in place. Rank all 10 within 5 guesses to win.',
    note: 'Daily Challenges give you a fresh reason to come back.',
  },
  {
    title: 'Pass & Play',
    eyebrow: 'SAME DEVICE',
    icon: SwapHorizRoundedIcon,
    background: altColorLighter,
    color: altColorDarkText,
    description:
      'Teams take turns finding the next top-ranked item. A correct guess scores +1 and earns another turn. The highest score wins.',
    note: 'Pass your device between the two sides. No lobby or account required.',
  },
  {
    title: 'Host a Game',
    eyebrow: 'BRING THE ROOM TOGETHER',
    icon: WorkspacePremiumRoundedIcon,
    background: '#fff6d7',
    color: '#70551a',
    description:
      'Only the host sees the ranked list. Let the room guess in any order and give hints to keep the game flowing.',
    note: 'Great for groups, game nights, and parties.',
  },
  {
    title: 'Play with Friends',
    eyebrow: 'SEPARATE DEVICES',
    icon: DevicesRoundedIcon,
    background: altColorLighter,
    color: altColorDarkText,
    description:
      'Create or join a private lobby, share the game code, and choose sides. Teams take turns finding the next ranked item on their own devices.',
    note: 'The host chooses the list and starts the game.',
  },
  ...(MATCHMAKING_ENABLED
    ? [
        {
          title: 'Online Match',
          eyebrow: 'ONLINE',
          icon: GroupsRoundedIcon,
          background: altColorLighter,
          color: altColorDarkText,
          description:
            'Challenge another player in ranked or unranked matchmaking. Press Find a Match to enter the queue.',
          note: 'Ranked matches compete for trophies.',
        },
      ]
    : []),
  {
    title: 'Polls',
    eyebrow: 'YOUR OPINION. OUR ORDER.',
    icon: HowToVoteRoundedIcon,
    background: altColorLighter,
    color: altColorDarkText,
    description:
      'Vote on the ranking, explore community results, or choose Play to guess the community order. You decide what to do next.',
    note: 'Community Answers questions let you submit your own answer, too.',
  },
]
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function HowToPlayDialog({
  open,
  fullScreen,
  sounds,
  onClose,
}: HowToPlayDialogProps): JSX.Element {
  const titleId = React.useId()
  const handleClose = () => {
    sounds?.click?.()
    onClose()
  }
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={Transition}
      maxWidth="md"
      PaperProps={{
        sx: {
          p: 0,
          borderRadius: '28px',
          border: `1px solid ${borderColor}`,
          background: canvasColor,
          overflow: 'hidden',
          ...(fullScreen && {
            m: 0,
            width: '100%',
            maxHeight: '100%',
            borderRadius: 0,
            '@media (max-width: 600px)': {
              m: '0 !important',
              maxHeight: '100% !important',
            },
          }),
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, sm: 3 },
          pt: fullScreen ? 'max(12px, env(safe-area-inset-top, 0px))' : 1.5,
          pb: 1.5,
          background: '#ffffff',
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <DialogTitle
          id={titleId}
          component="h2"
          sx={{ p: 0, fontSize: { xs: 24, sm: 28 } }}
        >
          How to Play
        </DialogTitle>
        <IconButton
          aria-label="Close How to Play"
          onClick={() => {
            sounds?.swipe?.()
            onClose()
          }}
          sx={{ width: 44, height: 44, background: canvasColor, color: navy }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          '&:first-of-type': { pt: { xs: 2, sm: 3 } },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: { xs: 2.5, sm: 3 },
            mb: 2.5,
            overflow: 'hidden',
            borderRadius: '24px',
            color: '#ffffff',
            background: `radial-gradient(circle at 95% 10%, #2b526f 0, transparent 55%), ${navy}`,
            boxShadow: '0 3px 0 #bfd4e3',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{ color: '#b7ddea', fontSize: 10, letterSpacing: 1 }}
            >
              A LITTLE KNOWLEDGE. A LOT OF FUN.
            </Typography>
            <Typography
              component="h3"
              variant="h2"
              sx={{ color: 'inherit', fontSize: { xs: 30, sm: 40 }, mt: 0.5 }}
            >
              Ready, set, rank.
            </Typography>
            <Typography variant="body2" sx={{ color: '#d2e4ef', mt: 1, maxWidth: 380 }}>
              Discover community lists. Put your knowledge to the test. Find your kind of
              play.
            </Typography>
          </Box>
          <Box
            component="img"
            src="/brand/podium.svg"
            alt=""
            aria-hidden
            sx={{ width: { xs: 76, sm: 122 }, flexShrink: 0 }}
          />
        </Box>
        <Box component="section" aria-labelledby={`${titleId}-start`} sx={{ mb: 3 }}>
          <Typography
            id={`${titleId}-start`}
            component="h3"
            variant="h5"
            sx={{ mb: 1.5 }}
          >
            Three steps. You&apos;re in.
          </Typography>
          <Box
            component="ol"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.5,
              p: 0,
              m: 0,
              listStyle: 'none',
            }}
          >
            {steps.map((step, i) => (
              <GlassPanel
                component="li"
                key={step.title}
                sx={{
                  p: 2,
                  background: '#ffffff',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 32,
                    height: 36,
                    borderRadius: '10px',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    fontFamily: headingFont,
                    fontWeight: 800,
                    fontSize: 20,
                    background: i === 1 ? gold : mainColorLighter,
                    color: i === 1 ? '#70551a' : mainColorDarker,
                    boxShadow: '0 2px 0 #d4e3ed',
                    transform: `rotate(${i === 1 ? 4 : -4}deg)`,
                  }}
                >
                  {i + 1}
                </Box>
                <Box minWidth={0}>
                  <Typography component="h4" variant="h6" sx={{ fontSize: 17 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {step.description}
                  </Typography>
                </Box>
              </GlassPanel>
            ))}
          </Box>
        </Box>
        <Box component="section" aria-labelledby={`${titleId}-modes`} sx={{ mb: 2.5 }}>
          <Typography
            id={`${titleId}-modes`}
            component="h3"
            variant="h5"
            sx={{ mb: 1.5 }}
          >
            A way to play for every mood.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {modes.map((mode) => (
              <GlassPanel
                component="article"
                key={mode.title}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  background: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box display="flex" gap={1.5} alignItems="center" mb={1.5}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 44,
                      height: 48,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '15px',
                      background: mode.background,
                      color: mode.color,
                      transform: 'rotate(-4deg)',
                    }}
                  >
                    <mode.icon sx={{ fontSize: 27 }} />
                  </Box>
                  <Box minWidth={0}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontSize: 9,
                        lineHeight: 1.4,
                        letterSpacing: 0.6,
                        color: helperTextColor,
                        display: 'block',
                      }}
                    >
                      {mode.eyebrow}
                    </Typography>
                    <Typography component="h4" variant="h6" sx={{ mt: 0.25 }}>
                      {mode.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {mode.description}
                </Typography>
                <Box
                  sx={{
                    p: 1.25,
                    mt: 1.5,
                    borderRadius: '12px',
                    background: mode.background,
                    color: mode.color,
                  }}
                >
                  <Typography variant="body2" fontSize={12} fontWeight={700}>
                    {mode.note}
                  </Typography>
                </Box>
              </GlassPanel>
            ))}
          </Box>
        </Box>
        <GlassPanel
          component="section"
          aria-label="Helpful tips"
          sx={{ p: 2, background: mainColorLighter, borderColor: '#b6e0ec' }}
        >
          <Typography component="h3" variant="h6" sx={{ mb: 1.5 }}>
            Good games start with good company.
          </Typography>
          <Box display="flex" gap={1.25} alignItems="flex-start" mb={1.25}>
            <ThumbsUpDownRoundedIcon
              aria-hidden
              sx={{ color: mainColorDarker, fontSize: 20, mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary">
              These lists are community-created. Rate the ones you play to help great
              trivia and polls stand out.
            </Typography>
          </Box>
          <Box display="flex" gap={1.25} alignItems="flex-start">
            <TipsAndUpdatesRoundedIcon
              aria-hidden
              sx={{ color: mainColorDarker, fontSize: 20, mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary">
              Hosting a round? Give a hint when the room needs a little nudge.
            </Typography>
          </Box>
        </GlassPanel>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 1.5,
          pb: fullScreen ? 'max(16px, env(safe-area-inset-bottom, 0px))' : 2,
          background: '#ffffff',
          borderTop: `1px solid ${borderColor}`,
          justifyContent: 'space-between',
        }}
      >
        <Button onClick={handleClose} sx={{ color: helperTextColor, minHeight: 44 }}>
          Close
        </Button>
        <GlassButton onClick={handleClose} sx={{ px: 3 }}>
          Got it!
        </GlassButton>
      </DialogActions>
    </Dialog>
  )
}
